import { useState, useMemo } from "react";
import { Icon } from "../icons";
import { FormLabel } from "./shared";
import { MUSCLE_GROUPS } from "../constants";
import { uid, today, buildPool, normalize } from "../helpers";


export default function WorkoutModal({ existing, allExercises, onClose, onSave }) {
  const [name, setName]               = useState(existing?.name || "");
  const [description, setDescription] = useState(existing?.description || "");
  const [selEx, setSelEx]             = useState(existing?.exercises || []);
  const [search, setSearch]           = useState("");
  const [muscleFilter, setMuscleFilter] = useState([]);
  const [touched, setTouched]         = useState({});

  const pool = useMemo(() => buildPool(allExercises), [allExercises]);
  const filtered = useMemo(() => pool.filter(e => {
    const matchesSearch = !search.trim() || normalize(e.name).includes(normalize(search.trim()));
    const matchesMuscle = muscleFilter.length === 0 || e.muscles.some(m => muscleFilter.includes(m));
    return matchesSearch && matchesMuscle;
  }), [pool, search, muscleFilter]);

  const nameErr = touched.name && !name.trim();
  const toggleEx = id => setSelEx(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const handleSave = () => {
    setTouched({ name: true });
    if (!name.trim()) return;
    // Derive muscles from selected exercises
    const derivedMuscles = [...new Set(
      selEx.flatMap(id => (pool.find(e => e.id === id)?.muscles || []).slice(0, 1))
    )];
    onSave({ id: existing?.id || uid(), name: name.trim(), description, muscles: derivedMuscles, exercises: selEx, createdAt: existing?.createdAt || today() });
    onClose();
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-md">
        <div className="modal-header">
          <div className="modal-title">{existing ? "Edit Workout" : "New Workout"}</div>
          <button className="modal-close" onClick={onClose}><Icon.X /></button>
        </div>

        <div className="modal-body">
          <div className="form-group">
            <FormLabel required>Workout Name</FormLabel>
            <input className={`input ${nameErr ? "error" : ""}`}
              placeholder="e.g. Push, Pull, Upper Body…"
              value={name} onChange={e => setName(e.target.value)}
              onBlur={() => setTouched(p => ({ ...p, name: true }))} autoFocus />
            {nameErr && <div className="field-error">Name is required</div>}
          </div>

          <div className="form-group">
            <div className="muscle-picker" style={{ marginTop:0, marginBottom:8 }}>
              {MUSCLE_GROUPS.filter(m => m !== "Full Body").map(m => (
                <div key={m} className={`muscle-chip ${muscleFilter.includes(m) ? "active" : ""}`}
                  onClick={() => setMuscleFilter(p => p.includes(m) ? p.filter(x => x !== m) : [...p, m])}>{m}</div>
              ))}
            </div>
            <input className="search-input" placeholder="Search exercises…"
              value={search} onChange={e => setSearch(e.target.value)}
              style={{ width:"100%", marginBottom:8, boxSizing:"border-box" }} />
            <div style={{ maxHeight:260, overflowY:"auto", display:"flex", flexDirection:"column", gap:4 }}>
              {filtered.length === 0 && (
                <div style={{ fontSize:11, color:"var(--muted)", padding:"10px 0" }}>
                  {pool.length === 0 ? "No exercises yet — add exercises first." : "No results."}
                </div>
              )}
              {filtered.map(ex => {
                const checked = selEx.includes(ex.id);
                return (
                  <div key={ex.id} onClick={() => toggleEx(ex.id)} style={{
                    display:"flex", alignItems:"center", gap:10, padding:"9px 12px",
                    borderRadius:8, cursor:"pointer",
                    background: checked ? "var(--accent-g)" : "var(--bg3)",
                    border: `1px solid ${checked ? "rgba(200,255,0,0.25)" : "var(--border)"}`,
                  }}>
                    <div style={{
                      width:16, height:16, borderRadius:3, flexShrink:0,
                      border: `1.5px solid ${checked ? "var(--accent)" : "var(--dim)"}`,
                      background: checked ? "var(--accent)" : "transparent",
                      display:"flex", alignItems:"center", justifyContent:"center",
                    }}>
                      {checked && <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                        <polyline points="1.5,5 4,7.5 8.5,2.5" stroke="#111" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:12, fontWeight:600, color: checked ? "var(--accent)" : "var(--text)" }}>
                        {ex.name}
                      </div>
                      <div style={{ fontSize:10, color:"var(--muted)" }}>
                        {(ex.muscles || []).slice(0, 3).join(" · ")}
                      </div>
                    </div>
                    <span style={{
                      fontSize:9, padding:"2px 7px", borderRadius:10, flexShrink:0,
                      background: ex.source === "mine" ? "rgba(0,200,150,0.12)" : "var(--bg2)",
                      color: ex.source === "mine" ? "rgba(0,200,150,0.9)" : "var(--dim)",
                      border: `1px solid ${ex.source === "mine" ? "rgba(0,200,150,0.3)" : "var(--border)"}`,
                    }}>
                      {ex.source === "mine" ? "mine" : ex.category || "catalogue"}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave}>Save</button>
        </div>
      </div>
    </div>
  );
}